/**
 * resubmit_run tool - Resubmit a failed flow run
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const resubmitRunInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID)"),
  runId: z.string().describe("The run ID (GUID) to resubmit"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
});

export type ResubmitRunInput = z.infer<typeof resubmitRunInputSchema>;

export const resubmitRunTool = {
  name: "resubmit_run",
  description:
    "Resubmit a failed or cancelled flow run. This will re-execute the flow with the same trigger inputs. Only works for flows with manual triggers.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID)",
      },
      runId: {
        type: "string",
        description: "The run ID (GUID) to resubmit",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
    },
    required: ["flowId", "runId"],
  },
};

export async function handleResubmitRun(
  api: FlowManagementApi,
  input: unknown
): Promise<string> {
  const parsed = resubmitRunInputSchema.parse(input);

  logger.info(
    { flowId: parsed.flowId, runId: parsed.runId },
    "Executing resubmit_run"
  );

  try {
    await api.resubmitRun(parsed.flowId, parsed.runId, parsed.environment);
  } catch (error) {
    // The generic formatter turns any 403 into "Access denied ... Premium",
    // which for resubmit is almost never the cause: the service refuses to
    // resubmit runs of manual (button) triggers, and says so in the body.
    const status = (error as { statusCode?: number }).statusCode;
    const details = (error as { details?: unknown }).details;
    const body = details === undefined ? "" : typeof details === "string" ? details : JSON.stringify(details);
    if (status === 403 || status === 400 || /manual|button|not supported|cannot be resubmitted/i.test(body)) {
      return (
        `The service refused to resubmit run ${parsed.runId}` +
        (status ? ` (HTTP ${status})` : "") +
        `. Runs of manually triggered (button) flows cannot be resubmitted — start a new run with run_flow instead. ` +
        `For automated triggers, resubmit is only available to the flow's owners.` +
        (body ? ` Service said: ${body.slice(0, 400)}` : "")
      );
    }
    throw error;
  }

  return `Resubmitted run ${parsed.runId} of flow ${parsed.flowId}. The new run appears in get_runs within a few seconds and re-uses the original trigger payload.`;
}
